import React, { useMemo } from 'react';
import { Box, Button, Typography, FormControl, Select, MenuItem } from '@mui/material';
import {
  PAGINATION_BAR_SX,
  PAGE_INDICATOR_SX,
  BTN_OUTLINED_SX,
} from '../styles/reportPageStyles';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * Shared list pagination — matches existing Data Display controls (prev / next / indicator).
 * Optional page-number chips and page-size select reuse the same outlined button styles.
 */
const ListPagination = ({
  page = 0,
  pageCount = 1,
  totalCount,
  pageSize,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
  showPageNumbers = true,
  showPageSize = false,
  disabled = false,
}) => {
  const safePageCount = Math.max(1, pageCount || 1);
  const safePage = Math.min(Math.max(0, page), safePageCount - 1);

  const pageNumbers = useMemo(() => {
    if (!showPageNumbers || safePageCount <= 1) return [];
    const maxButtons = 5;
    if (safePageCount <= maxButtons) {
      return Array.from({ length: safePageCount }, (_, i) => i);
    }
    let start = Math.max(0, safePage - Math.floor(maxButtons / 2));
    let end = start + maxButtons;
    if (end > safePageCount) {
      end = safePageCount;
      start = Math.max(0, end - maxButtons);
    }
    return Array.from({ length: end - start }, (_, i) => start + i);
  }, [safePage, safePageCount, showPageNumbers]);

  if (totalCount === 0) {
    return (
      <Box sx={PAGINATION_BAR_SX}>
        <Button variant="outlined" disabled sx={BTN_OUTLINED_SX}>
          قبلی
        </Button>
        <Typography sx={PAGE_INDICATOR_SX}>صفحه ۰ از ۰</Typography>
        <Button variant="outlined" disabled sx={BTN_OUTLINED_SX}>
          بعدی
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...PAGINATION_BAR_SX,
        flexWrap: 'wrap',
        gap: { xs: 1, sm: 2 },
      }}
    >
      <Button
        variant="outlined"
        onClick={() => onPageChange(Math.max(0, safePage - 1))}
        disabled={disabled || safePage <= 0}
        sx={BTN_OUTLINED_SX}
      >
        قبلی
      </Button>

      {showPageNumbers && pageNumbers.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
          {pageNumbers[0] > 0 && (
            <>
              <Button
                size="small"
                variant="outlined"
                disabled={disabled}
                onClick={() => onPageChange(0)}
                sx={{ ...BTN_OUTLINED_SX, minWidth: 36, px: 1 }}
              >
                ۱
              </Button>
              {pageNumbers[0] > 1 && (
                <Typography sx={{ ...PAGE_INDICATOR_SX, px: 1, py: 0.5 }}>…</Typography>
              )}
            </>
          )}
          {pageNumbers.map((p) => (
            <Button
              key={p}
              size="small"
              variant={p === safePage ? 'contained' : 'outlined'}
              disabled={disabled}
              onClick={() => onPageChange(p)}
              sx={
                p === safePage
                  ? {
                      ...BTN_OUTLINED_SX,
                      minWidth: 36,
                      px: 1,
                      bgcolor: '#00a86a',
                      color: '#fff',
                      borderColor: '#00a86a',
                      '&:hover': { bgcolor: '#037f4c', borderColor: '#037f4c' },
                    }
                  : { ...BTN_OUTLINED_SX, minWidth: 36, px: 1 }
              }
            >
              {(p + 1).toLocaleString('fa-IR')}
            </Button>
          ))}
          {pageNumbers[pageNumbers.length - 1] < safePageCount - 1 && (
            <>
              {pageNumbers[pageNumbers.length - 1] < safePageCount - 2 && (
                <Typography sx={{ ...PAGE_INDICATOR_SX, px: 1, py: 0.5 }}>…</Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                disabled={disabled}
                onClick={() => onPageChange(safePageCount - 1)}
                sx={{ ...BTN_OUTLINED_SX, minWidth: 36, px: 1 }}
              >
                {safePageCount.toLocaleString('fa-IR')}
              </Button>
            </>
          )}
        </Box>
      )}

      <Typography sx={PAGE_INDICATOR_SX}>
        صفحه {(safePage + 1).toLocaleString('fa-IR')} از {safePageCount.toLocaleString('fa-IR')}
        {typeof totalCount === 'number' ? ` — ${totalCount.toLocaleString('fa-IR')} مورد` : ''}
      </Typography>

      <Button
        variant="outlined"
        onClick={() => onPageChange(Math.min(safePageCount - 1, safePage + 1))}
        disabled={disabled || safePage >= safePageCount - 1}
        sx={BTN_OUTLINED_SX}
      >
        بعدی
      </Button>

      {showPageSize && typeof onPageSizeChange === 'function' && (
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={disabled}
            sx={{
              bgcolor: '#fff',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            {pageSizeOptions.map((n) => (
              <MenuItem key={n} value={n}>
                {n.toLocaleString('fa-IR')} در صفحه
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default ListPagination;
